/**
 * Single source of truth for the Ring 2 (verified external sources) vocabulary.
 *
 * Ring 2 is the slice-5 "trusted Dutch GGZ content beyond Linda's curated
 * corpus" tier. Sources are crawled weekly into `kennisbank_ring2`; the
 * agent retrieves Ring 1 + Ring 2 in one pass and renders a Petrol chip
 * for Ring 2 citations next to the existing Mosterdgeel chip for Ring 1.
 *
 * Three consumers must agree on this list:
 *   - agents/kennisagent/data/ring2-sources.yml — Python crawler config
 *   - frontend citation chip rendering (label + tooltip)
 *   - backend audit context for crawl runs
 *
 * Adding a source is a code change everywhere it appears; that's the
 * point — Ring 2 vocabulary is small and clinical, not generic web crawl.
 *
 * v1: NHG, Trimbos, Thuisarts.nl, GGZ-standaarden, RIVM. Farmako deferred
 * to v1.1 (medication-info domain has separate citation/legal nuances).
 * 2026-05-22 add: Behandelhulp.nl (Linda request, practitioner GGZ
 * treatment articles).
 */
export const RING2_SOURCES = [
    'nhg',
    'trimbos',
    'thuisarts',
    'ggz_standaarden',
    'rivm',
    'behandelhulp',
    // 2026-06-19 additions — Batch 1 (sitemap, low effort) + Batch 2 (HTML discovery)
    'akwa_ggz',
    'pharos',
    'vilans',
    'nji',
    'kc_bipolair',
    'kc_persoonlijkheid',
    'nip',
    'bpsw',
    'zorgvoorbeter',
    'augeo',
    'fier',
    'zelfmoordpreventie_113',
    'eetstoornissen_netwerk',
    'landelijk_netwerk_autisme',
    'movisie',
    // Verwijs-link source (professional content only — contact pages excluded)
    'centrum_seksueel_geweld',
    // 2026-06-22 additions — verslavingszorg + kenniscentra (PR4)
    'jellinek',
    'novadic_kentron',
    'verslavingskunde_nederland',
    'kc_phrenos',
    'kc_lvb',
    // 2026-08-06 additions — Trimbos-familie, na de dekkingsaudit
    'ledd',
    'nemesis',
    'helderopvoeden',
    'platformsmr',
    'nationale_drugmonitor',
    'heliomare',
    // 2026-08-28: bron achter de NJi-databank Richtlijnen
    'richtlijnen_jeugdhulp',
];
/** Display label for the citation chip + tooltip. Keep short. */
export const RING2_SOURCE_LABELS = {
    nhg: 'NHG',
    trimbos: 'Trimbos',
    thuisarts: 'Thuisarts.nl',
    ggz_standaarden: 'GGZ-standaarden',
    rivm: 'RIVM',
    behandelhulp: 'Behandelhulp.nl',
    akwa_ggz: 'Akwa GGZ',
    pharos: 'Pharos',
    vilans: 'Vilans',
    nji: 'Nederlands Jeugdinstituut',
    kc_bipolair: 'KC Bipolaire Stoornissen',
    kc_persoonlijkheid: 'KC Persoonlijkheidsstoornissen',
    nip: 'NIP',
    bpsw: 'BPSW',
    zorgvoorbeter: 'Zorg voor Beter',
    augeo: 'Augeo Foundation',
    fier: 'Fier',
    zelfmoordpreventie_113: '113 Zelfmoordpreventie',
    eetstoornissen_netwerk: 'Eetstoornissen Netwerk',
    landelijk_netwerk_autisme: 'Landelijk Netwerk Autisme',
    movisie: 'Movisie',
    centrum_seksueel_geweld: 'Centrum Seksueel Geweld',
    jellinek: 'Jellinek',
    novadic_kentron: 'Novadic-Kentron',
    verslavingskunde_nederland: 'Verslavingskunde Nederland',
    kc_phrenos: 'Kenniscentrum Phrenos',
    kc_lvb: 'Kenniscentrum LVB',
    ledd: 'LEDD (Dubbele Diagnose)',
    nemesis: 'NEMESIS (Trimbos)',
    helderopvoeden: 'Helder Opvoeden',
    platformsmr: 'Platform Stoppen Met Roken',
    nationale_drugmonitor: 'Nationale Drug Monitor',
    heliomare: 'Heliomare',
    richtlijnen_jeugdhulp: 'Richtlijnen jeugdhulp',
};
/** Set form for O(1) membership checks. */
export const RING2_SOURCES_SET = new Set(RING2_SOURCES);
//# sourceMappingURL=ring2-sources.js.map